/**
 * AccountContextSwitcher — compact Personal ↔ Business pill for the
 * /account header. Only rendered when the signed-in user has BOTH a
 * vendor profile AND a member profile.
 *
 * • One tap flips context (optimistic + POST /api/me/context).
 * • Visually inspired by iOS segmented control — tight, obvious,
 *   keyboard-accessible.
 */
import { Building2, User } from "lucide-react";
import { toast } from "sonner";
import Tip from "@/components/Tip";

export default function AccountContextSwitcher({ state, onChange, compact = false }) {
  if (!state?.has_vendor_profile || !state?.has_member_profile) return null;
  const active = state.active_context === "business" ? "business" : "personal";

  async function pick(target) {
    if (target === active) return;
    try {
      await onChange?.(target);
      toast.success(
        target === "business"
          ? "Switched to Business profile"
          : "Switched to Personal profile"
      );
    } catch (_e) {
      toast.error("Couldn't switch context. Try again.");
    }
  }

  const base = compact
    ? "inline-flex items-center rounded-full bg-[#F3F0EA] dark:bg-stone-800 p-0.5 gap-0.5"
    : "inline-flex items-center rounded-full bg-[#F3F0EA] dark:bg-stone-800 p-1 gap-1";
  const btn = (on) =>
    `inline-flex items-center gap-1.5 rounded-full ${compact ? "px-2.5 h-7 text-[11px]" : "px-3 h-8 text-xs"} font-semibold transition-colors ${
      on
        ? "bg-white dark:bg-stone-700 text-[#121614] dark:text-stone-100 shadow-sm"
        : "text-stone-500 dark:text-stone-400 hover:text-[#121614] dark:hover:text-stone-200"
    }`;

  return (
    <div
      data-testid="account-context-switcher"
      data-active={active}
      role="group"
      aria-label="Account profile"
      className={base}
    >
      <Tip label="Personal profile">
        <button
          type="button"
          data-testid="account-context-personal"
          aria-pressed={active === "personal"}
          onClick={() => pick("personal")}
          className={btn(active === "personal")}
        >
          <User className="h-3.5 w-3.5" strokeWidth={2} />
          Personal
        </button>
      </Tip>
      <Tip label="Business profile">
        <button
          type="button"
          data-testid="account-context-business"
          aria-pressed={active === "business"}
          onClick={() => pick("business")}
          className={btn(active === "business")}
        >
          <Building2 className="h-3.5 w-3.5" strokeWidth={2} />
          Business
        </button>
      </Tip>
    </div>
  );
}
